import {
  bulkClaimSaveSchema,
  claimEditSchema,
  individualBulkClaimSaveSchema,
} from '@billing/validation';
import { useForm } from '@mantine/form';
import type { FormErrors, UseFormReturnType } from '@mantine/form';
import { createBlankTariff } from 'components/common/ModalComponents/utils/tariffHelpers';
import type {
  BulkClaimForm,
  BulkEditClaimForm as BulkEditForm,
  Claim,
  CreateClaimForm as CreateForm,
  Credit,
  Debit,
  EditClaimForm,
} from 'interfaces';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { useMemo } from 'react';
import { resolveSiteDefaultPayor } from 'store';
import { nowISO, parseDateTime } from 'utils/dateUtils';
import { v4 as uuidv4 } from 'uuid';
import { hasOnlyIncompleteTariffs } from '@billing/validation/incompleteClaim';
import { tariffSchemaWithRequiredICD9 } from '@billing/validation/tariff';

import type {
  BulkClaimFormOptions,
  BulkEditClaimFormOptions,
  CreateClaimFormOptions,
  EditClaimFormOptions,
  FormClaimTariff,
  GetClaimTariffIcd9Errors,
  TariffIcd9ValidationOptions,
  UseValidatedClaimForm,
  ValidatedClaimValues,
} from './ClaimFormTypes';

/** Checks one claim using its own site and tariff statuses. Bulk callers supply a path prefix. */
const getClaimTariffIcd9Errors: GetClaimTariffIcd9Errors = (
  values,
  options
) => {
  const { includeIncompleteTariffIcd9Errors = false, prefix = '' } = options;
  // An absent site flag requires ICD9, matching the shared claim schemas.
  const requiresTariffIcd9 = values.site?.requiresTariffIcd9 ?? true;
  if (!requiresTariffIcd9 || !Array.isArray(values.tariffs)) return {};
  // All-incomplete creations skip required ICD9 guidance; edits can still display it.
  if (!includeIncompleteTariffIcd9Errors && hasOnlyIncompleteTariffs(values))
    return {};

  const errors: FormErrors = {};
  for (const [index, tariff] of values.tariffs.entries()) {
    if (!tariff || typeof tariff !== 'object' || Array.isArray(tariff))
      continue;
    const result = tariffSchemaWithRequiredICD9.shape.icd9.safeParse(
      tariff.icd9
    );
    if (!result.success) {
      errors[`${prefix}tariffs.${index}.icd9`] = result.error.issues[0].message;
    }
  }
  return errors;
};

/** Collects form-wide ICD9 errors, retaining each bulk claim's index for field feedback. */
export const getTariffIcd9Errors = (
  values: ValidatedClaimValues,
  options: TariffIcd9ValidationOptions = {}
): FormErrors => {
  if (!values || typeof values !== 'object' || Array.isArray(values)) return {};
  if (!Array.isArray(values.claims))
    return getClaimTariffIcd9Errors(values, options);

  const errors: FormErrors = {};
  for (const [index, claim] of values.claims.entries()) {
    if (!claim || typeof claim !== 'object' || Array.isArray(claim)) continue;
    Object.assign(
      errors,
      getClaimTariffIcd9Errors(claim, {
        ...options,
        prefix: `claims.${index}.`,
      })
    );
  }
  return errors;
};

const getInitialValues = (
  claim: Claim,
  overrides: Partial<EditClaimForm>
): EditClaimForm => {
  // Normalize unselected values to null before applying intentional modal defaults.
  const initialValues: Partial<EditClaimForm> = Object.fromEntries(
    Object.entries(claim).map(([key, value]) => [
      key,
      value === '' ? null : value,
    ])
  );
  return { ...claim, ...initialValues, ...overrides };
};

// Blank rows start with missing required fields and are checked by the shared schema.
const createBlankFormTariff = (
  claimServiceStart?: string,
  claimServiceEnd?: string
): FormClaimTariff =>
  createBlankTariff(
    undefined,
    claimServiceStart,
    claimServiceEnd
  ) as FormClaimTariff;

const useValidatedClaimForm: UseValidatedClaimForm = (input) => {
  const { includeIncompleteTariffIcd9Errors = false, ...options } = input;
  const form = useForm({
    ...options,
    // Check ICD9 separately because an invalid sibling field can skip Zod refinements.
    validate: (values) => ({
      ...getTariffIcd9Errors(values, { includeIncompleteTariffIcd9Errors }),
      // Give the full schema precedence when both checks report the same field.
      ...options.validate(values),
    }),
    clearInputErrorOnChange: false,
    validateInputOnChange: false,
    onValuesChange: (values, previousValues) => {
      // Refresh sibling and dependent errors after values commit, before notifying callers.
      form.validate();
      options.onValuesChange?.(values, previousValues);
    },
  });
  return form;
};

// WCB needs editable payment drafts only when the claim has no existing debit rows.
const getInitialDebits = (claim: Claim): Debit[] | undefined => {
  if (claim.debits?.length) return claim.debits;
  if (claim.payor?.name !== 'WCB' || !claim.tariffs.length) return claim.debits;

  return claim.tariffs.map(
    (tariff) =>
      ({
        id: uuidv4(),
        requestedAmount: tariff.rate,
        paidAmount: '0',
        claimID: claim.id,
        payorID: claim.payor?.id,
        providerID: claim.provider?.id ?? null,
        microfilm: '',
        adjustment: null,
        createdAt: nowISO(),
        status: 'pending',
        type: 'PAYMENT',
        source: 'MANUAL',
        claim,
        tariffID: tariff.id ?? null,
        eobs: [],
        tariff,
        payor: claim.payor,
        provider: claim.provider,
      } as Debit)
  );
};

// WCB credit drafts stay aligned with tariffs, even when debit rows already exist.
const getInitialCredits = (claim: Claim): Credit[] => {
  if (claim.payor?.name !== 'WCB' || !claim.tariffs.length) return [];

  return claim.tariffs.map(() => ({
    id: uuidv4(),
    amount: '0',
    date: nowISO(),
    description: 'WCB Payment',
    filename: '',
    providerID: claim.provider?.id,
    provider: claim.provider,
    site: claim.site,
    siteID: claim.site.id,
  }));
};

export const ClaimForm = ({
  row,
  onValuesChange,
}: EditClaimFormOptions): UseFormReturnType<EditClaimForm> => {
  const defaults = {
    resubmit: false,
    internalComments: '',
    healthcard: row.original.healthcard ?? null,
    debits: getInitialDebits(row.original),
    credit: getInitialCredits(row.original),
    healthcards: row.original?.patient?.healthcards || [],
    group: row.original.group || null,
  };

  const initialValues = useMemo(
    () => getInitialValues(row.original, defaults),
    [row.original, defaults]
  );

  const form = useValidatedClaimForm<EditClaimForm>({
    mode: 'uncontrolled',
    initialValues: initialValues,
    validate: zod4Resolver(claimEditSchema),
    includeIncompleteTariffIcd9Errors: true,
    initialDirty: {
      tariffs: false,
      healthcard: false,
    },
    onValuesChange,
  });
  return form;
};

export const BulkEditClaimForm = ({
  claimIDs,
  initialValues = {},
  onValuesChange,
}: BulkEditClaimFormOptions): UseFormReturnType<BulkEditForm> => {
  // Keep the sparse patch separate from selected IDs so untouched fields stay unchanged.
  const formInitialValues = useMemo<BulkEditForm>(
    () => ({
      claimIDs,
      values: initialValues,
    }),
    [claimIDs, initialValues]
  );

  return useForm<BulkEditForm>({
    mode: 'uncontrolled',
    initialValues: formInitialValues,
    clearInputErrorOnChange: true,
    onValuesChange,
  });
};

export const CreateClaimForm = ({
  status,
  site,
  payors,
  initialValues,
  onValuesChange,
}: CreateClaimFormOptions): UseFormReturnType<CreateForm> => {
  // Build the default service window in site time, independent of the browser timezone.
  const startTime = parseDateTime(nowISO(site.timezone), {
    zone: site.timezone,
  });
  const endTime = startTime.plus({ minutes: 15 });
  const defaultPayor = resolveSiteDefaultPayor(site, payors);
  const defaults: CreateForm = {
    serviceStart: startTime.toISO(),
    createdAt: startTime.toISO(),
    serviceEnd: endTime.toISO(),
    status: status,
    siteID: site.id,
    site: site,
    version: 1,
    patientCount: 1,
    overhead: 0,
    patient: null,
    patientID: null,
    providerID: null,
    healthcardID: null,
    payorID: defaultPayor?.id ?? null,
    payor: defaultPayor,
    tariffs: [createBlankFormTariff(startTime.toISO(), endTime.toISO())],
    groupID: null,
  };

  const form = useValidatedClaimForm<CreateForm>({
    initialValues: {
      ...defaults,
      // Explicit selections and restored drafts take precedence over creation defaults.
      ...initialValues,
    },
    validate: zod4Resolver(individualBulkClaimSaveSchema),
    onValuesChange,
  });

  return form;
};

export const BulkForm = ({
  status,
  site,
  payor,
  initialSharedValues,
  onValuesChange,
}: BulkClaimFormOptions): UseFormReturnType<BulkClaimForm> => {
  const startTime = parseDateTime(nowISO(site.timezone), {
    zone: site.timezone,
  });
  const endTime = startTime.plus({ minutes: 15 });
  const defaults: BulkClaimForm = {
    shared: {
      serviceStart: startTime.toISO(),
      createdAt: startTime.toISO(),
      serviceEnd: endTime.toISO(),
      status: status,
      siteID: site.id,
      version: 1,
      patientCount: 1,
      overhead: site.overhead,
      provider: null,
      providerID: null,
      site: site,
      patientID: null,
      healthcardID: null,
      payorID: payor?.id ?? null,
      payor,
      tariffs: [],
      referringProvider: null,
      referringProviderObject: null,
      messageID: null,
      ...initialSharedValues,
    },
    // Shared fields seed individual claims as the bulk workflow progresses.
    claims: [],
  };

  const form = useValidatedClaimForm<BulkClaimForm>({
    initialValues: defaults,
    validate: zod4Resolver(bulkClaimSaveSchema),
    onValuesChange,
  });

  return form;
};
